import { useState, useEffect } from 'react'
// Define types for all the data contracts
export interface IntimacyData {
  level: number
  stage: string
  current: number
  nextLevel: number
  weeklyEarned: number
  rank: {
    percentile: number
    label: string
  }
  streak: {
    daily: number
    weekly: number
    heatmap: boolean[]
  }
}
export interface AchievementData {
  unlockedCount: number
  total: number
  rarityBreakdown: {
    common: number
    rare: number
    epic: number
    legendary: number
  }
  recent: {
    id: string
    name: string
    icon: string
    rarity: 'common' | 'rare' | 'epic' | 'legendary'
  }
  next: {
    id: string
    name: string
    icon: string
    progress: number
    total: number
    rarity: 'common' | 'rare' | 'epic' | 'legendary'
  }
}
export interface RelationshipData {
  top: Array<{
    id: string
    name: string
    avatar: string
  }>
  details: Record<
    string,
    {
      ageDays: number
      messagesCount: number
      giftsSent: number
      milestones: Array<{
        id: string
        name: string
        unlocked: boolean
        condition?: string
      }>
      insights: string[]
    }
  >
}
export interface LootboxData {
  total: number
  opened: number
  missedCount: number
  bestPull: {
    name: string
    rarity: 'common' | 'rare' | 'epic' | 'legendary'
  }
  breakdown: {
    tokens: number
    gems: number
    skins: number
    vipDays: number
  }
  nextUnlock: {
    ritualsLeft: number
    reward: string
  }
}
export interface RewardData {
  lifetime: {
    tokens: number
    gems: number
  }
  totalClaimed: number
  skinsOwned: number
  vipDays: number
  sourceBreakdown: {
    rituals: number
    events: number
    purchases: number
    promos: number
  }
  recent: Array<{
    id: string
    name: string
    amount: number
    type: 'tokens' | 'gems' | 'skin' | 'vip'
    source: 'lootbox' | 'calendar' | 'rituals' | 'promo'
    date: string
  }>
}
export interface ActivityData {
  messages: {
    '7days': number
    '30days': number
    alltime: number
    trend: number
  }
  gifts: {
    '7days': number
    '30days': number
    alltime: number
    trend: number
  }
  games: {
    '7days': number
    '30days': number
    alltime: number
    trend: number
    won: {
      '7days': number
      '30days': number
      alltime: number
    }
  }
  rituals: {
    '7days': {
      daily: number
      weekly: number
      premium: number
    }
    '30days': {
      daily: number
      weekly: number
      premium: number
    }
    alltime: {
      daily: number
      weekly: number
      premium: number
    }
  }
  heatmap: number[]
}
export interface NudgeData {
  id: string
  title: string
  description: string
  ctaLabel: string
  deepLink: string
  priority: number
}
export interface StatsData {
  loading: boolean
  error: boolean
  intimacy: IntimacyData | null
  achievements: AchievementData | null
  relationships: RelationshipData | null
  lootboxes: LootboxData | null
  rewards: RewardData | null
  activity: ActivityData | null
  nudges: NudgeData[] | null
}
// Mock data hook
export const useStatsData = (): StatsData => {
  const [data, setData] = useState<StatsData>({
    loading: true,
    error: false,
    intimacy: null,
    achievements: null,
    relationships: null,
    lootboxes: null,
    rewards: null,
    activity: null,
    nudges: null,
  })
  useEffect(() => {
    // Simulate API call
    const fetchData = async () => {
      try {
        // In a real app, this would be an API call
        await new Promise((resolve) => setTimeout(resolve, 800))
        // Mock data
        setData({
          loading: false,
          error: false,
          intimacy: {
            level: 5,
            stage: 'Flame Stage',
            current: 250,
            nextLevel: 500,
            weeklyEarned: 45,
            rank: {
              percentile: 18,
              label: 'Top 18% of community',
            },
            streak: {
              daily: 7,
              weekly: 2,
              heatmap: [true, true, true, true, true, true, true],
            },
          },
          achievements: {
            unlockedCount: 8,
            total: 24,
            rarityBreakdown: {
              common: 4,
              rare: 3,
              epic: 1,
              legendary: 0,
            },
            recent: {
              id: 'ach-1',
              name: 'Golden Lover',
              icon: 'Heart',
              rarity: 'epic',
            },
            next: {
              id: 'ach-2',
              name: 'Power Player',
              icon: 'Zap',
              progress: 75,
              total: 100,
              rarity: 'rare',
            },
          },
          relationships: {
            top: [
              {
                id: 'angel-1',
                name: 'Sophia',
                avatar: 'https://placehold.co/100x100/9333ea/ffffff?text=S',
              },
              {
                id: 'angel-2',
                name: 'Harper',
                avatar: 'https://placehold.co/100x100/ec4899/ffffff?text=H',
              },
              {
                id: 'angel-3',
                name: 'Zoey',
                avatar: 'https://placehold.co/100x100/3b82f6/ffffff?text=Z',
              },
            ],
            details: {
              'angel-1': {
                ageDays: 32,
                messagesCount: 247,
                giftsSent: 12,
                milestones: [
                  { id: 'ms-1', name: 'First Date', unlocked: true },
                  { id: 'ms-2', name: 'Intimate Connection', unlocked: true },
                  {
                    id: 'ms-3',
                    name: 'Soul Mates',
                    unlocked: false,
                    condition: 'Send 50 more messages',
                  },
                ],
                insights: [
                  'She responds well to humor',
                  'Prefers voice messages over text',
                  'Most active in the evenings',
                ],
              },
              'angel-2': {
                ageDays: 18,
                messagesCount: 124,
                giftsSent: 5,
                milestones: [
                  { id: 'ms-1', name: 'First Date', unlocked: true },
                  {
                    id: 'ms-2',
                    name: 'Intimate Connection',
                    unlocked: false,
                    condition: 'Send 5 more gifts',
                  },
                ],
                insights: [
                  'Loves receiving photos',
                  'Responds quickly to messages',
                ],
              },
              'angel-3': {
                ageDays: 7,
                messagesCount: 43,
                giftsSent: 2,
                milestones: [
                  { id: 'ms-1', name: 'First Date', unlocked: true },
                ],
                insights: [
                  'New relationship with potential',
                  'Shares your interest in gaming',
                ],
              },
            },
          },
          lootboxes: {
            total: 14,
            opened: 12,
            missedCount: 2,
            bestPull: {
              name: 'Lingerie Skin: Zoey',
              rarity: 'epic',
            },
            breakdown: {
              tokens: 1250,
              gems: 75,
              skins: 3,
              vipDays: 5,
            },
            nextUnlock: {
              ritualsLeft: 2,
              reward: 'Weekly Premium Lootbox',
            },
          },
          rewards: {
            lifetime: {
              tokens: 12450,
              gems: 320,
            },
            totalClaimed: 32,
            skinsOwned: 7,
            vipDays: 45,
            sourceBreakdown: {
              rituals: 45,
              events: 25,
              purchases: 20,
              promos: 10,
            },
            recent: [
              {
                id: 'reward-1',
                name: 'Daily Login Bonus',
                amount: 25,
                type: 'tokens',
                source: 'calendar',
                date: '2023-09-15T09:45:00Z',
              },
              {
                id: 'reward-2',
                name: 'Match-3 High Score',
                amount: 5,
                type: 'gems',
                source: 'events',
                date: '2023-09-14T20:12:00Z',
              },
              {
                id: 'reward-3',
                name: 'VIP Weekly Bonus',
                amount: 100,
                type: 'tokens',
                source: 'promo',
                date: '2023-09-12T15:30:00Z',
              },
            ],
          },
          activity: {
            messages: {
              '7days': 124,
              '30days': 542,
              alltime: 1240,
              trend: 15,
            },
            gifts: {
              '7days': 8,
              '30days': 32,
              alltime: 75,
              trend: -5,
            },
            games: {
              '7days': 12,
              '30days': 45,
              alltime: 120,
              trend: 20,
              won: {
                '7days': 8,
                '30days': 30,
                alltime: 85,
              },
            },
            rituals: {
              '7days': {
                daily: 5,
                weekly: 1,
                premium: 2,
              },
              '30days': {
                daily: 22,
                weekly: 4,
                premium: 8,
              },
              alltime: {
                daily: 75,
                weekly: 12,
                premium: 24,
              },
            },
            heatmap: [30, 45, 20, 60, 75, 50, 90],
          },
          nudges: [
            {
              id: 'nudge-1',
              title: 'Weekly Premium Lootbox',
              description:
                'Complete 2 more rituals to unlock your Weekly Premium Lootbox.',
              ctaLabel: 'Go to Rituals',
              deepLink: 'rituals',
              priority: 1,
            },
            {
              id: 'nudge-2',
              title: 'Gift Appreciation',
              description:
                'Send 3 more gifts to unlock "Gift Appreciation" with Harper.',
              ctaLabel: 'Send a Gift',
              deepLink: 'chat',
              priority: 2,
            },
            {
              id: 'nudge-3',
              title: 'Login Streak',
              description:
                'Log in 2 more days to reach your 14-day streak reward.',
              ctaLabel: 'View Streak Rewards',
              deepLink: 'rewards',
              priority: 3,
            },
          ],
        })
      } catch (error) {
        setData((prev) => ({
          ...prev,
          loading: false,
          error: true,
        }))
      }
    }
    fetchData()
  }, [])
  return data
}
